import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';
import type { ApiFullCartItem } from '../../../api/cart/responses/cartResponses';
import type { OrderEntry } from '../../../models/domain/order';
import type { PriceData } from '../../../models/priceData/priceData';
import type { GaEcommerceItem, GaEcommerceItemDetails } from '../shared/ga4Shared';

import { Helpers } from '../../../../../utility/helpers';
import { productFlow } from '../../../flow/product/productFlow';
import {
    mapApiProductVariantPriceData,
    mapPrices,
    resolveApiProductVariant,
} from '../../../models/domain/ecommerce/promotion/promotionMappers';

export type GaEcommerceContextOptions = {
    isUserMember: boolean;
};

export type GaUnitPriceAndDiscountInput = {
    listPrice: number | undefined;
    transactionPrice: number | undefined;
};

export const toGaUnitPriceAndDiscount = ({
    listPrice,
    transactionPrice,
}: GaUnitPriceAndDiscountInput): { unitPrice: number | null; unitDiscount?: number } => {
    if (!transactionPrice) {
        return { unitPrice: null };
    }

    const unitPrice = Number(transactionPrice.toFixed(2));

    if (!listPrice || listPrice <= transactionPrice) {
        return { unitPrice };
    }

    const unitDiscount = Number((listPrice - transactionPrice).toFixed(2));

    if (unitDiscount <= 0) {
        return { unitPrice };
    }

    return { unitPrice, unitDiscount };
};

const getGaUnitPricesFromPriceData = (
    priceData: PriceData | undefined,
    promotionPriceData: PriceData | undefined,
) => {
    const listPrice = priceData ? Helpers.getPriceForGa(priceData) : undefined;
    const promotionPrice = promotionPriceData
        ? Helpers.getPriceForGa(promotionPriceData)
        : undefined;

    const transactionPrice =
        promotionPrice && listPrice && promotionPrice < listPrice ? promotionPrice : listPrice;

    return { listPrice, transactionPrice };
};

type ProductToGaEcommerceItemParams = {
    product: ApiProduct;
    quantity: number;
    options: GaEcommerceContextOptions;
    variantId?: string;
    index?: number;
};

export const mapProductToGaEcommerceItem = ({
    product,
    quantity,
    options,
    variantId,
    index,
}: ProductToGaEcommerceItemParams): GaEcommerceItem => {
    const { priceData, promotionPriceData } = mapApiProductVariantPriceData(product, variantId);

    // filter out price if not applicable like member or b2b
    const applicablePromotionPriceData =
        product.promo?.isMemberPrice && !options.isUserMember ? undefined : promotionPriceData;

    const { listPrice, transactionPrice } = getGaUnitPricesFromPriceData(
        priceData,
        applicablePromotionPriceData,
    );

    const { unitPrice, unitDiscount } = toGaUnitPriceAndDiscount({
        listPrice,
        transactionPrice,
    });

    const selectedVariant = resolveApiProductVariant(product, variantId);

    const data: GaEcommerceItem = {
        item_id: product.identifier,
        price: unitPrice,
        quantity,
        index,
        item_variant: selectedVariant?.name?.toLowerCase(),
        ...(unitDiscount != null ? { discount: unitDiscount } : {}),
    };

    return data;
};

type GaLineEntryPrices = {
    itemId: string | undefined;
    quantity: number;
    variantName?: string;
    index?: number;
    piecePrice?: PriceData;
    discount?: PriceData;
    totalPrice?: PriceData;
    totalPriceWithoutDiscount?: PriceData;
};

const mapLineEntryToGaEcommerceItem = ({
    itemId,
    quantity,
    variantName,
    index,
    piecePrice,
    discount,
    totalPrice,
    totalPriceWithoutDiscount,
}: GaLineEntryPrices): GaEcommerceItem => {
    if (!piecePrice) {
        return {
            item_id: itemId,
            price: null,
            quantity,
            index,
            item_variant: variantName,
        };
    }

    const mappedPrices = mapPrices({
        piecePrice,
        discount,
        totalPrice,
        totalPriceWithoutDiscount,
    });

    const listPrice = Helpers.getPriceForGa(mappedPrices.piecePrice);
    const transactionPrice = Helpers.getPriceForGa(
        mappedPrices.newPiecePrice ?? mappedPrices.piecePrice,
    );
    const { unitPrice, unitDiscount } = toGaUnitPriceAndDiscount({
        listPrice,
        transactionPrice,
    });

    return {
        item_id: itemId,
        price: unitPrice,
        quantity,
        index,
        item_variant: variantName,
        ...(unitDiscount != null ? { discount: unitDiscount } : {}),
    };
};

export const mapCartItemToGaEcommerceItem = (
    cartItem: ApiFullCartItem,
    index?: number,
): GaEcommerceItem =>
    mapLineEntryToGaEcommerceItem({
        itemId: cartItem.productId,
        quantity: cartItem.quantity,
        variantName: cartItem.variantName?.toLowerCase(),
        index,
        piecePrice: cartItem.piecePrice ?? cartItem.priceData,
        discount: cartItem.discount,
        totalPrice: cartItem.totalPriceData,
        totalPriceWithoutDiscount: cartItem.totalPriceWithoutDiscount,
    });

export const mapOrderEntryToGaEcommerceItem = (
    entry: OrderEntry,
    index?: number,
): GaEcommerceItem =>
    mapLineEntryToGaEcommerceItem({
        itemId: entry.product?.code,
        quantity: entry.quantity,
        variantName: entry.variance?.name?.toLowerCase(),
        index,
        piecePrice: entry.piecePrice,
        discount: entry.discount,
        totalPrice: entry.totalPrice,
        totalPriceWithoutDiscount: entry.totalPriceWithoutDiscount,
    });

export const mapProductToGaItemWithDetails = ({
    product,
    quantity,
    options,
    variantId,
}: Omit<ProductToGaEcommerceItemParams, 'index'>) => {
    const gaItem = mapProductToGaEcommerceItem({ product, quantity, options, variantId });

    const mapped: GaEcommerceItem & GaEcommerceItemDetails = {
        ...gaItem,
        item_name: product.name,
        item_brand: product.manufacturer,
        item_category: product.sections?.[0]?.[0]?.name,
        item_category2: product.sections?.[0]?.[1]?.name,
        item_category3: product.sections?.[0]?.[2]?.name,
    };

    return mapped;
};

/**
 * Use only in GA context, never in presentational code
 */
export const getGaTotalProductsValue = (items: GaEcommerceItem[]) => {
    return Number(
        items.reduce((acc, curr) => acc + (curr.price || 0) * (curr.quantity || 1), 0).toFixed(2),
    );
};

export const getGaMinimalProductKey = (product: { productId: string; variantId?: string }) =>
    product.variantId ? `${product.productId}-${product.variantId}` : product.productId;

export const getGaProductsWithVariantsMap = async <
    TItem extends {
        productId: string;
        variantId: string | undefined;
    },
>(
    inputProductData: TItem[],
    storeId: string,
) => {
    const productsMap = await productFlow.getProductsByIdsMap(
        inputProductData.map((item) => item.productId),
        storeId,
    );

    const map: typeof productsMap = new Map();

    inputProductData.forEach(({ productId, variantId }) => {
        const product = productsMap.get(productId);

        if (product) {
            map.set(getGaMinimalProductKey({ productId, variantId }), product);
        }
    });

    return map;
};

// Keeps the order of inputData array
export const getGaProductsWithVariantsList = async <
    TItem extends {
        productId: string;
        variantId: string | undefined;
    },
>(
    inputProductData: TItem[],
    storeId: string,
) => {
    const map = await getGaProductsWithVariantsMap(inputProductData, storeId);

    const list = inputProductData
        .map((inputData) => {
            const product = map.get(
                getGaMinimalProductKey({
                    productId: inputData.productId,
                    variantId: inputData.variantId,
                }),
            );

            if (!product) return null;

            return { productData: product, inputData };
        })
        .filter(Boolean);

    return list;
};

// Merge products from previous and current state with previous and new quantity
// Includes removed, added and updated products (looking at quantity to determine)
export const getGaChangedItems = (
    previousProducts: {
        productId: string;
        variantId: string | undefined;
        quantity: number;
    }[],
    currentProducts: {
        productId: string;
        variantId: string | undefined;
        quantity: number;
    }[],
) => {
    let mergedProducts: {
        productId: string;
        variantId: string | undefined;
        previousQuantity: number;
        newQuantity: number;
    }[] = [];

    // Iterate through the previous products array and add the quantities to the map
    const previousProductsMap: Map<string, (typeof mergedProducts)[0]> = new Map();
    previousProducts.forEach((product) => {
        const key = getGaMinimalProductKey(product);
        // If the product already exists in the map, add the quantities
        previousProductsMap.set(key, {
            ...product,
            previousQuantity: product.quantity,
            newQuantity: 0,
        });
    });

    // Iterate through the current products array and add the quantities from previous products
    currentProducts.forEach((product) => {
        const key = getGaMinimalProductKey(product);

        const previousProduct = previousProductsMap.get(key);
        const previousQuantity = previousProduct ? previousProduct.previousQuantity : 0;

        mergedProducts.push({
            productId: product.productId,
            variantId: product.variantId,
            previousQuantity,
            newQuantity: product.quantity,
        });

        if (previousProduct) {
            previousProductsMap.delete(key);
        }
    });

    // Add any remaining products from the previous products array with a new quantity of 0
    previousProductsMap.forEach((product) => {
        mergedProducts.push({
            productId: product.productId,
            variantId: product.variantId,
            previousQuantity: product.previousQuantity,
            newQuantity: 0,
        });
    });

    // if quantity is the same, then it's not a changed product
    mergedProducts = mergedProducts.filter((item) => item.previousQuantity !== item.newQuantity);

    return mergedProducts;
};
